import React, { useMemo, useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { createSelector } from 'reselect';
import { Box } from '@mantine/core';
import { Link } from 'react-router-dom';
import { Badge } from 'reactstrap';
import { ODataGrid } from '../../../Components/OElements/ODataGrid';
import { Icons } from '../../../utils/Icons';
import { getProcedureHistoryListQuery } from '../../../slices/thunks';
import { setRequireRefetch } from '../../../slices/procedureHistory/reducer';
import AddWaterRightHistoryModal from '../Modals/AddWaterRightHistoryModal';
import DeleteProcedureHistoryModal from '../Modals/DeleteProcedureHistoryModal';

const ProcedureHistoryPanel = ({ waterRightData, toggleRefetch }) => {
	const dispatch = useDispatch();
	const [modalHistory, setModalHistory] = useState(false);
	const [modalDelete, setModalDelete] = useState(false);
	const [selectedHistoryData, setSelectedHistoryData] = useState(null);
	const [refetchTrigger, setRefetchTrigger] = useState(0);

	// Redux selector
	const selectorProcedureHistory = createSelector(
		state => state.ProcedureHistory,
		procedureHistory => ({
			requireRefetch: procedureHistory?.requireRefetch,
		})
	);

	const { requireRefetch } = useSelector(selectorProcedureHistory);

	// Check requireRefetch on mount
	useEffect(() => {
		if (requireRefetch) {
			setRefetchTrigger(prev => prev + 1);
			dispatch(setRequireRefetch(false));
		}
	}, []); // Only run on mount

	// Trigger refetch when operations complete
	useEffect(() => {
		if (requireRefetch) {
			setRefetchTrigger(prev => prev + 1);
			dispatch(setRequireRefetch(false));
		}
	}, [requireRefetch, dispatch]);

	const handleToggleHistoryModal = () => {
		setModalHistory(!modalHistory);
		if (modalHistory) {
			setSelectedHistoryData(null);
		}
	};

	const handleToggleDeleteModal = () => {
		setModalDelete(!modalDelete);
		if (modalDelete) {
			setSelectedHistoryData(null);
		}
	};

	const handleClickAdd = () => {
		setSelectedHistoryData(null);
		setModalHistory(true);
	};

	const handleClickEdit = historyData => {
		setSelectedHistoryData(historyData);
		setModalHistory(true);
	};

	const handleClickDelete = historyData => {
		setSelectedHistoryData(historyData);
		setModalDelete(true);
	};

	// Helper function to format dates
	const formatDate = (dateString) => {
		if (!dateString) return '-';
		const date = new Date(dateString);
		if (isNaN(date.getTime())) return '-';
		const day = String(date.getDate()).padStart(2, '0');
		const month = String(date.getMonth() + 1).padStart(2, '0');
		const year = date.getFullYear();
		return `${day}/${month}/${year}`;
	};

	const columns = useMemo(
		() => [
			{
				header: 'Acciones',
				enableSorting: false,
				enableColumnFilter: false,
				enableGlobalFilter: false,
				enableColumnActions: false,
				accessorKey: 'actions',
				columnDefType: 'display',
				size: 'auto',
				Cell: ({ cell, row }) => {
					const historyData = row.original;
					return (
						<div className='d-flex justify-content-center'>
							<div className='flex-shrink-0'>
								<ul className='list-inline mb-0'>
									<li className='list-inline-item'>
										<Link
											title={'Editar'}
											to='#'
											onClick={() => handleClickEdit(historyData)}
										>
											<i className='ri-pencil-fill align-bottom me-2 text-muted'></i>
										</Link>
									</li>
									<li className='list-inline-item'>
										<Link
											title={'Eliminar'}
											to='#'
											className='remove-item-btn'
											onClick={() => handleClickDelete(historyData)}
										>
											<i className='ri-delete-bin-line align-bottom me-2 text-muted'></i>
										</Link>
									</li>
								</ul>
							</div>
						</div>
					);
				},
			},
			{
				header: 'Nombre',
				dataType: 'string',
				accessorKey: 'name',
			},
			{
				header: 'Tipo de Trámite',
				dataType: 'select',
				accessorKey: 'procedure_type.name',
			},
			{
				header: 'Fecha',
				dataType: 'date',
				accessorKey: 'procedure_date',
				Cell: cellProps => {
					return <span>{formatDate(cellProps.row.original.procedure_date)}</span>;
				},
			},
		],
		[formatDate]
	);

	return (
		<>
			<Box
				className='p-2'
				sx={{
					display: 'Grid',
					margin: 'auto',
					width: '100%',
				}}
			>
				<ODataGrid
					columns={columns}
					getDataFunc={getProcedureHistoryListQuery}
					baseFilters={{
						water_right_id: {
							$eq: waterRightData._id,
						},
						disable: {
							$eq: false,
						},
					}}
					queryPopulate={['procedure_type']}
					remoteOperations={{
						pagination: true,
						sorting: true,
						filtering: true,
					}}
					nameFile='historial_tramites'
					getRowIdTable={(row) => row._id}
					refetchRemoteData={refetchTrigger}
				/>
			</Box>

			{modalHistory && (
				<AddWaterRightHistoryModal
					waterRightData={waterRightData}
					dataToEdit={selectedHistoryData}
					isOpen={modalHistory}
					handleToggleModal={handleToggleHistoryModal}
				/>
			)}

			{modalDelete && selectedHistoryData && (
				<DeleteProcedureHistoryModal
					dataProcedureHistory={selectedHistoryData}
					isOpen={modalDelete}
					handlerToggleModal={handleToggleDeleteModal}
				/>
			)}
		</>
	);
};

export default ProcedureHistoryPanel;
